import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { CalendarDays, Users, UsersRound, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../api/examApi';
import { facultyApi } from '../api/facultyApi';

const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const formatDateTime = (iso) =>
  new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

// updated_at only differs from created_at (by more than a minute) when a
// record was corrected after the fact -- a once-marked record shouldn't
// display two near-identical timestamps.
const wasCorrected = (record) =>
  Math.abs(new Date(record.updated_at) - new Date(record.created_at)) > 60 * 1000;

const TABS = [
  { key: 'date', label: 'By Date', icon: CalendarDays },
  { key: 'faculty', label: 'By Faculty', icon: Users },
  { key: 'group', label: 'By Group', icon: UsersRound },
];

function StatusBadge({ status }) {
  const isPresent = status === 'present';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        isPresent
          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
          : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
      }`}
    >
      {isPresent ? <CheckCircle size={11} /> : <XCircle size={11} />}
      {isPresent ? 'Present' : 'Absent'}
    </span>
  );
}

export default function AdminAttendancePage() {
  const [tab, setTab] = useState('date');

  const [singleDate, setSingleDate] = useState(todayIso());
  const [dateFrom, setDateFrom] = useState(daysAgoIso(7));
  const [dateTo, setDateTo] = useState(todayIso());

  const [groups, setGroups] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [groupId, setGroupId] = useState('');
  const [facultyId, setFacultyId] = useState('');

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    adminApi.listGroups().then(({ data }) => setGroups(data)).catch(() => toast.error('Failed to load groups'));
    facultyApi.getAll({ page_size: 100 }).then(({ data }) => setFaculty(data.data)).catch(() => toast.error('Failed to load faculty'));
  }, []);

  const canFetch = tab === 'date' || (tab === 'faculty' && facultyId) || (tab === 'group' && groupId);

  const load = useCallback(async () => {
    if (!canFetch) {
      setRecords([]);
      return;
    }
    setLoading(true);
    try {
      const params = tab === 'date'
        ? { date_from: singleDate, date_to: singleDate }
        : { date_from: dateFrom, date_to: dateTo };
      if (tab === 'faculty') params.faculty_id = facultyId;
      if (tab === 'group') params.group_id = groupId;
      const { data } = await adminApi.getAttendance(params);
      setRecords(data);
    } catch {
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  }, [tab, singleDate, dateFrom, dateTo, facultyId, groupId, canFetch]);

  useEffect(() => { load(); }, [load]);

  const presentCount = useMemo(() => records.filter((r) => r.status === 'present').length, [records]);
  const absentCount = records.length - presentCount;

  return (
    <DashboardLayout title="Attendance Oversight">
      <div className="animate-fade-in space-y-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900 dark:text-white">
            Attendance Oversight
          </h2>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Read-only aggregate view — marking stays on the faculty attendance page.
          </p>
        </div>

        <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`attendance-tab-${key}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === key
                  ? 'border-primary text-primary dark:border-accent-cool dark:text-accent-cool'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
          <div className="flex flex-wrap items-center gap-4">
            {tab === 'date' && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Date</label>
                <input
                  type="date"
                  value={singleDate}
                  max={todayIso()}
                  onChange={(e) => setSingleDate(e.target.value)}
                  className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool"
                  data-testid="attendance-single-date"
                />
              </div>
            )}

            {tab !== 'date' && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">From</label>
                <input
                  type="date"
                  value={dateFrom}
                  max={dateTo}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool"
                  data-testid="attendance-date-from"
                />
                <label className="text-sm font-medium text-slate-700 dark:text-slate-300">To</label>
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom}
                  max={todayIso()}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool"
                  data-testid="attendance-date-to"
                />
              </div>
            )}

            {tab === 'faculty' && (
              <select
                value={facultyId}
                onChange={(e) => setFacultyId(e.target.value)}
                className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool"
                data-testid="attendance-faculty-select"
              >
                <option value="">Select faculty…</option>
                {faculty.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            )}

            {tab === 'group' && (
              <select
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-primary dark:focus:ring-accent-cool"
                data-testid="attendance-group-select"
              >
                <option value="">Select group…</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            )}

            {!loading && records.length > 0 && (
              <p className="text-sm text-slate-500 dark:text-slate-400 ml-auto">
                {presentCount} present &middot; {absentCount} absent &middot; {records.length} total
              </p>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden shadow-sm">
          {loading ? (
            <div className="animate-pulse">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center p-4 border-b border-slate-100 dark:border-slate-800">
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mr-4"></div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mr-4"></div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/4"></div>
                </div>
              ))}
            </div>
          ) : !canFetch ? (
            <div className="py-16 text-center text-slate-400 text-sm">
              {tab === 'faculty' ? 'Select a faculty member to view their students’ attendance.' : 'Select a group to view its members’ attendance.'}
            </div>
          ) : records.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm">No attendance records found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50">
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Student</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Date</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Status</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Marked By</th>
                  <th className="px-5 py-3 text-left font-medium text-slate-500">Marked At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {records.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors" data-testid={`attendance-record-${r.id}`}>
                    <td className="px-5 py-3 font-medium text-slate-900 dark:text-white">{r.student_name}</td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">{r.date}</td>
                    <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
                      {r.marked_by_name || '—'} <span className="text-xs capitalize">({r.marked_by_type})</span>
                    </td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
                      <div data-testid={`attendance-marked-at-${r.id}`}>{formatDateTime(r.created_at)}</div>
                      {wasCorrected(r) && (
                        <div className="text-xs text-amber-600 dark:text-amber-400" data-testid={`attendance-corrected-at-${r.id}`}>
                          corrected {formatDateTime(r.updated_at)}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
